import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDepot } from './depot-format';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  EventFilters as Filters,
  EventStatus,
  EventAudience,
  EventType,
  DepotRef,
} from '@/types';

/** Sortierung, getrennt vom `Filters`-Objekt (15.09.2026) — im Typsystem schon
 *  immer getrennt: `EventListParams` fügt `sortBy`/`sortOrder` erst zu den
 *  Filtern hinzu. `undefined` = Standard (Datum/Depot/Titel, siehe Backend
 *  sortEvents() — Reihenfolge geändert 22.09.2026). */
export interface EventSort {
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

interface Props {
  filters: Filters;
  depots: DepotRef[];
  /** Offered as the VA-Typ filter; retired types are included so an existing
      event stays findable. */
  eventTypes?: EventType[];
  onChange: (filters: Filters) => void;
  sort: EventSort;
  onSortChange: (sort: EventSort) => void;
}

const ALL = '__all__';
const SORT_DEFAULT = 'default';

export function EventFilters({
  filters,
  depots,
  eventTypes = [],
  onChange,
  sort,
  onSortChange,
}: Props) {
  const { t } = useTranslation('events');
  const [searchInput, setSearchInput] = useState(filters.search ?? '');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Ein Schnellzugriff-Button (EventListPage.tsx) ersetzt `filters` komplett,
  // von außen — ohne das hier nachzuziehen, würde das Suchfeld weiterhin
  // alten Text zeigen, obwohl er nicht mehr greift (21.09.2026-Follow-up).
  // Nur bei einer Löschung von außen, nicht bei jeder Änderung: sonst würde
  // das Debounce unten sich mit einer normalen Eingabe selbst überschreiben.
  useEffect(() => {
    if (!filters.search) setSearchInput('');
  }, [filters.search]);

  // Debounced title-prefix search (min 2 chars, or empty to clear).
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const trimmed = searchInput.trim();
      const next = trimmed.length === 0 ? undefined : trimmed;
      if (next !== filters.search) onChange({ ...filters, search: next });
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const setSingle = <K extends keyof Filters>(
    key: K,
    value: string,
    empty: Filters[K]
  ) => {
    onChange({ ...filters, [key]: value === ALL ? empty : value });
  };
  void setSingle; // reserved for future multi-value filters

  return (
    <div className="space-y-3">
      {/* Zeile 1: Titel suchen, VA-Typ, Status. */}
      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder={t('filters.searchPlaceholder')}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="max-w-xs"
        />

        {eventTypes.length > 0 && (
          <Select
            value={filters.vaTypes?.[0] ?? ALL}
            onValueChange={(v) =>
              onChange({ ...filters, vaTypes: v === ALL ? undefined : [v] })
            }
          >
            {/* Wider than the enum filters: type names run to "Weinverkostung
                & Kulinaria". */}
            <SelectTrigger className="w-[230px]">
              <SelectValue placeholder={t('filters.vaType')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t('filters.allVaTypes')}</SelectItem>
              {eventTypes.map((et) => (
                <SelectItem key={et.id} value={et.id}>
                  {et.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <Select
          value={filters.status?.[0] ?? ALL}
          onValueChange={(v) =>
            onChange({ ...filters, status: v === ALL ? undefined : [v as EventStatus] })
          }
        >
          {/* Breit genug für "Zur Prüfung weitergeleitet", den längsten Eintrag
              (25.09.2026, Nutzervorgabe) — 180px ließ ihn umbrechen. */}
          <SelectTrigger className="w-[230px]">
            <SelectValue placeholder={t('filters.status')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('filters.allStatus')}</SelectItem>
            {Object.values(EventStatus).map((s) => (
              <SelectItem key={s} value={s}>
                {t(`status.${s}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Zeile 2: Depots, Datum, Herkunft, Teilnehmerkreis. */}
      <div className="flex flex-wrap items-center gap-3">
        {depots.length > 0 && (
          <Select
            value={filters.depotKeys?.[0] ?? ALL}
            onValueChange={(v) =>
              onChange({ ...filters, depotKeys: v === ALL ? undefined : [v] })
            }
          >
            {/* Wider than the other filters: it holds "D001 — Düsseldorf
                Lohausen/Kaiserswerth", not just a depot number. */}
            <SelectTrigger className="w-[260px]">
              <SelectValue placeholder={t('filters.depot')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t('filters.allDepots')}</SelectItem>
              {depots.map((d) => (
                <SelectItem key={d.key} value={d.key}>
                  {formatDepot(d)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Date range, dates only. Matches events whose start OR end falls inside
            the window, so a multi-day event is found from either side. */}
        <div className="flex items-center gap-2">
          <Input
            type="date"
            aria-label={t('filters.dateFrom')}
            title={t('filters.dateFrom')}
            className="w-[150px]"
            value={filters.dateFrom ?? ''}
            onChange={(e) => {
              const value = e.target.value;
              // "Bis" mitziehen, solange es noch leer ist — dasselbe Muster wie
              // beim Datumsbereich im VA-Formular (EventFormDialog.tsx).
              onChange({
                ...filters,
                dateFrom: value || undefined,
                dateTo: value && !filters.dateTo ? value : filters.dateTo,
              });
            }}
          />
          <span className="text-muted-foreground">–</span>
          <Input
            type="date"
            aria-label={t('filters.dateTo')}
            title={t('filters.dateTo')}
            className="w-[150px]"
            value={filters.dateTo ?? ''}
            onChange={(e) => onChange({ ...filters, dateTo: e.target.value || undefined })}
          />
        </div>

        <Select
          value={filters.audience?.[0] ?? ALL}
          onValueChange={(v) =>
            onChange({ ...filters, audience: v === ALL ? undefined : [v as EventAudience] })
          }
        >
          {/* Breit genug für "Alle Teilnehmerkreise" (25.09.2026, Nutzervorgabe)
              — 170px ließ ihn umbrechen. */}
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder={t('filters.audience')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('filters.allAudience')}</SelectItem>
            {Object.values(EventAudience).map((v) => (
              <SelectItem key={v} value={v}>
                {t(`audience.${v}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Sortierung (15.09.2026) — rechtsbündig mit Präfix, damit klar ist,
            was die Auswahl bewirkt (22.09.2026-Follow-up). "Standard" ist
            Datum/Depot/Titel (siehe Backend sortEvents()); die anderen vier
            sind Einzelfeld-Sortierungen. */}
        <div className="ml-auto flex items-center gap-2">
          <span className="whitespace-nowrap text-sm text-muted-foreground">
            {t('filters.sort')}:
          </span>
          <Select
            value={sort.sortBy ? `${sort.sortBy}:${sort.sortOrder ?? 'asc'}` : SORT_DEFAULT}
            onValueChange={(v) => {
              if (v === SORT_DEFAULT) {
                onSortChange({});
                return;
              }
              const [sortBy, sortOrder] = v.split(':');
              onSortChange({ sortBy, sortOrder: sortOrder as 'asc' | 'desc' });
            }}
          >
            <SelectTrigger className="w-[190px]">
              <SelectValue placeholder={t('filters.sort')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={SORT_DEFAULT}>{t('filters.sortDefault')}</SelectItem>
              <SelectItem value="startAt:asc">{t('filters.sortDateAsc')}</SelectItem>
              <SelectItem value="startAt:desc">{t('filters.sortDateDesc')}</SelectItem>
              <SelectItem value="status:asc">{t('filters.sortStatusAsc')}</SelectItem>
              <SelectItem value="status:desc">{t('filters.sortStatusDesc')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}
